/**
 * Status view — Clear tiles with edge RAG (PRD PRES-001, PRES-003, PRES-008).
 * Left edge = ipSCAPE, right edge = Wildix. Collapsible left menu (campaigns/teams), sort preference, Lucide icons.
 */
import { useState, useMemo, useEffect } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { Menu, LayoutList, CheckCircle, XCircle, Grid3X3, UserCheck } from 'lucide-react';
import { getStatusIcon } from '../lib/statusIcons';
import { Tooltip } from '../components/Tooltip';
import AgentTile from '../components/AgentTile';
import type { AgentStatusRow } from '../components/AgentTile';
import {
  visibleCampaignTitlesForStatusSidebar,
  type CampaignFilterVisibilityConfig,
} from '../lib/campaignFilterVisibility';
import { parseAgentDirectoryPayload } from '../lib/agentDirectory';
import {
  agentBelongsToCampaignTitle,
  buildCampaignIdToTitleMap,
  formatCodesAsCampaignNames,
} from '../lib/campaignCodeToName';

type CampaignCatalogRow = {
  campaign_id: number;
  campaign_title: string | null;
  campaign_group_name: string | null;
};

const STORAGE_SORT = 'ipscape-status-sort';
const STORAGE_MENU_TYPE = 'ipscape-status-menu-type';
const STORAGE_SIDEBAR_OPEN = 'ipscape-status-sidebar-open';
const STORAGE_STATUS_FILTER = 'ipscape-status-filter';
const STORAGE_LOGGED_IN_ONLY = 'ipscape-status-logged-in-only';
const STORAGE_SELECTED_FILTER_CAMPAIGN = 'ipscape-selected-filter-campaign';
const STORAGE_SELECTED_FILTER_TEAM = 'ipscape-selected-filter-team';

/** Expanded sidebar width (~25% wider than original 200px) */
const SIDEBAR_WIDTH_OPEN = 250;
const FEED_STALE_MS = 3 * 60 * 1000;

type WsFeedMeta = {
  ucPollFetched: boolean;
  ucPollAt: number | null;
  ucPollSkippedReason: string | null;
  ucPollDurationMs?: number | null;
  ucPollAvgDurationMs?: number | null;
  ucConsecutiveFailures?: number;
  ucConsecutiveSuccesses?: number;
  pollPipelineTrusted?: boolean;
  platformUsersSyncedAt?: string | null;
};

function formatLastUcPollLabel(atMs: number): { clock: string; ago: string } {
  const d = new Date(atMs);
  const clock = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const sec = Math.max(0, Math.floor((Date.now() - atMs) / 1000));
  const ago =
    sec < 60 ? `${sec}s ago` : sec < 3600 ? `${Math.floor(sec / 60)}m ago` : `${Math.floor(sec / 3600)}h ago`;
  return { clock, ago };
}

function mergeLiveAndCachedAgents(live: AgentStatusRow[], cached: AgentStatusRow[]): AgentStatusRow[] {
  const m = new Map<string, AgentStatusRow>();
  for (const a of cached) m.set(a.id, a);
  for (const a of live) m.set(a.id, { ...m.get(a.id), ...a } as AgentStatusRow);
  return Array.from(m.values()).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

export type SortOption = 'name-az' | 'name-za' | 'campaign-az' | 'campaign-za' | 'team-az' | 'team-za';
export type MenuType = 'campaign' | 'team';
export type StatusFilterOption = 'all' | 'available' | 'unavailable';

/** ipSCAPE status counts as "available" for the status filter toggle */
function isAvailableStatus(ipscapeStatus: string): boolean {
  const s = ipscapeStatus.toLowerCase();
  return ['available', 'online', 'registered'].includes(s);
}

function readLoggedInOnly(): boolean {
  try {
    return localStorage.getItem(STORAGE_LOGGED_IN_ONLY) === '1';
  } catch {}
  return false;
}

function readStatusFilter(): StatusFilterOption {
  try {
    const v = localStorage.getItem(STORAGE_STATUS_FILTER);
    if (v === 'all' || v === 'available' || v === 'unavailable') return v;
  } catch {}
  return 'all';
}

function readSort(): SortOption {
  try {
    const v = localStorage.getItem(STORAGE_SORT);
    if (v && ['name-az', 'name-za', 'campaign-az', 'campaign-za', 'team-az', 'team-za'].includes(v))
      return v as SortOption;
  } catch {}
  return 'name-az';
}

function readMenuType(): MenuType {
  try {
    const v = localStorage.getItem(STORAGE_MENU_TYPE);
    if (v === 'campaign' || v === 'team') return v;
  } catch {}
  return 'campaign';
}

function readSidebarOpen(): boolean {
  try {
    const v = localStorage.getItem(STORAGE_SIDEBAR_OPEN);
    if (v === '0') return false;
    if (v === '1') return true;
  } catch {}
  return true;
}

function readSelectedFilter(menuType: MenuType): string {
  try {
    const key = menuType === 'campaign' ? STORAGE_SELECTED_FILTER_CAMPAIGN : STORAGE_SELECTED_FILTER_TEAM;
    const v = localStorage.getItem(key);
    return typeof v === 'string' ? v : '';
  } catch {}
  return '';
}

export default function Status() {
  const { user } = useUser();
  const { getToken } = useAuth();
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>(readSort);
  const [menuType, setMenuType] = useState<MenuType>(readMenuType);
  const [sidebarOpen, setSidebarOpen] = useState(readSidebarOpen);
  const [selectedFilter, setSelectedFilter] = useState(() => readSelectedFilter(readMenuType()));
  const [statusFilter, setStatusFilter] = useState<StatusFilterOption>(readStatusFilter);
  const [loggedInOnly, setLoggedInOnly] = useState(readLoggedInOnly);
  const [statusIcons, setStatusIcons] = useState<{ campaigns?: Record<string, string>; teams?: Record<string, string> }>({});
  const [presenceWsUrlFromConfig, setPresenceWsUrlFromConfig] = useState<string>('');
  const [liveAgents, setLiveAgents] = useState<AgentStatusRow[]>([]);
  const [cachedAgents, setCachedAgents] = useState<AgentStatusRow[]>([]);
  const [presenceConnected, setPresenceConnected] = useState(false);
  const [wsFeedMeta, setWsFeedMeta] = useState<WsFeedMeta | null>(null);
  /** Re-render once per second so "Ns ago" after last UC meta ticks without waiting for WebSocket. */
  const [ucMetaTick, setUcMetaTick] = useState(0);
  const [catalogRows, setCatalogRows] = useState<CampaignCatalogRow[]>([]);
  const [campaignFilterVisibility, setCampaignFilterVisibility] = useState<CampaignFilterVisibilityConfig | null>(null);

  const presenceWsUrl = (presenceWsUrlFromConfig && presenceWsUrlFromConfig.startsWith('ws'))
    ? presenceWsUrlFromConfig
    : (typeof import.meta !== 'undefined' ? (import.meta.env?.VITE_PRESENCE_WS_URL as string | undefined) : undefined);
  const workerOrigin = useMemo(() => {
    if (!presenceWsUrl?.startsWith('ws')) return '';
    try {
      const normalized = presenceWsUrl.startsWith('wss://')
        ? `https://${presenceWsUrl.slice('wss://'.length)}`
        : `http://${presenceWsUrl.slice('ws://'.length)}`;
      return new URL(normalized).origin;
    } catch {
      return '';
    }
  }, [presenceWsUrl]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const base = typeof window !== 'undefined' ? window.location.origin : '';
      try {
        const res = await fetch(`${base}/api/config?keys=status_icons,presence_ws_url,campaign_filter_visibility`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setStatusIcons((data.status_icons as { campaigns?: Record<string, string>; teams?: Record<string, string> }) || {});
          if (typeof data.presence_ws_url === 'string' && data.presence_ws_url.trim()) {
            setPresenceWsUrlFromConfig(data.presence_ws_url.trim());
          }
          const vis = data.campaign_filter_visibility;
          if (vis && typeof vis === 'object' && !Array.isArray(vis)) {
            setCampaignFilterVisibility(vis as CampaignFilterVisibilityConfig);
          } else {
            setCampaignFilterVisibility(null);
          }
        }
      } catch {
        // keep default
      }
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!workerOrigin) return;
      try {
        const res = await fetch(`${workerOrigin}/campaigns/catalog`);
        if (res.ok) {
          const data = (await res.json()) as { campaigns?: CampaignCatalogRow[] };
          if (Array.isArray(data.campaigns) && !cancelled) setCatalogRows(data.campaigns);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workerOrigin]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!workerOrigin) return;
      try {
        const res = await fetch(`${workerOrigin}/agents/catalog`);
        if (!res.ok) return;
        const data = (await res.json()) as {
          agents?: { agent_id: number; payload: string; synced_at: string | null; is_logged_in?: boolean | null }[];
        };
        const list: AgentStatusRow[] = [];
        for (const r of data.agents ?? []) {
          const a = parseAgentDirectoryPayload(r.agent_id, r.payload, r.is_logged_in);
          if (a) list.push(a);
        }
        if (!cancelled) setCachedAgents(list);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workerOrigin]);

  useEffect(() => {
    const id = window.setInterval(() => setUcMetaTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_SORT, sortBy);
    } catch {}
  }, [sortBy]);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_MENU_TYPE, menuType);
    } catch {}
  }, [menuType]);
  useEffect(() => {
    try {
      const key = menuType === 'campaign' ? STORAGE_SELECTED_FILTER_CAMPAIGN : STORAGE_SELECTED_FILTER_TEAM;
      localStorage.setItem(key, selectedFilter);
    } catch {}
  }, [menuType, selectedFilter]);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_SIDEBAR_OPEN, sidebarOpen ? '1' : '0');
    } catch {}
  }, [sidebarOpen]);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_STATUS_FILTER, statusFilter);
    } catch {}
  }, [statusFilter]);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_LOGGED_IN_ONLY, loggedInOnly ? '1' : '0');
    } catch {}
  }, [loggedInOnly]);

  useEffect(() => {
    if (!presenceWsUrl?.startsWith('ws')) return;
    let ws: WebSocket | null = null;
    let cancelled = false;
    const connect = () => {
      try {
        ws = new WebSocket(presenceWsUrl!);
        ws.onopen = () => setPresenceConnected(true);
        ws.onclose = () => {
          setPresenceConnected(false);
          if (!cancelled) setTimeout(connect, 5000);
        };
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data as string) as {
              type?: string;
              agents?: AgentStatusRow[];
              meta?: WsFeedMeta;
            };
            if (data.type === 'snapshot' && Array.isArray(data.agents)) {
              setLiveAgents(data.agents.map((a) => ({ ...a, statusDuration: a.statusDuration ?? 0, avatar_url: null })));
            }
            if (data.type === 'snapshot' && data.meta) {
              setWsFeedMeta(data.meta);
            }
          } catch { /* ignore */ }
        };
      } catch {
        setPresenceConnected(false);
      }
    };
    connect();
    return () => {
      cancelled = true;
      if (ws) ws.close();
    };
  }, [presenceWsUrl]);

  const agentsSource = useMemo(
    () => mergeLiveAndCachedAgents(liveAgents, cachedAgents),
    [liveAgents, cachedAgents]
  );

  const pollFresh =
    presenceConnected &&
    wsFeedMeta?.ucPollAt != null &&
    Date.now() - wsFeedMeta.ucPollAt < FEED_STALE_MS;

  const feedLive =
    pollFresh && wsFeedMeta?.ucPollFetched === true && !wsFeedMeta?.ucPollSkippedReason;

  const snapshotMode = !feedLive;

  const closedReasonLabel = useMemo(() => {
    const r = wsFeedMeta?.ucPollSkippedReason;
    if (r === 'outside_operating_hours') return 'Outside operating hours';
    if (r === 'ipscape_disabled') return 'ipSCAPE polling disabled';
    if (!presenceConnected) return 'Not connected to presence';
    if (!pollFresh && wsFeedMeta?.ucPollAt != null) return 'Last update is stale';
    if (!wsFeedMeta?.ucPollFetched && r) return 'Waiting for next poll';
    if (!wsFeedMeta?.ucPollFetched) return 'Connecting to live feed';
    return 'Snapshot only';
  }, [wsFeedMeta, presenceConnected, pollFresh]);
  const failStreak = wsFeedMeta?.ucConsecutiveFailures ?? 0;
  const pollTrusted = wsFeedMeta?.pollPipelineTrusted !== false;

  /** UC pipeline health for Live badge (WebSocket + meta from Presence DO). */
  const liveBadge: 'green' | 'amber' | 'gray' | 'red' = (() => {
    if (!presenceConnected) return 'gray';
    if (failStreak >= 5) return 'red';
    if (!pollFresh || !wsFeedMeta?.ucPollFetched) return 'gray';
    if (!pollTrusted || failStreak >= 3) return 'amber';
    return 'green';
  })();

  const wallboardClosed =
    wsFeedMeta?.ucPollSkippedReason === 'outside_operating_hours' ||
    wsFeedMeta?.ucPollSkippedReason === 'ipscape_disabled';
  const onlySocketConnected = presenceConnected && liveBadge === 'gray' && !wallboardClosed;
  const lastUcPollUi = useMemo(() => {
    const at = wsFeedMeta?.ucPollAt;
    if (at == null) return null;
    return formatLastUcPollLabel(at);
  }, [wsFeedMeta?.ucPollAt, ucMetaTick]);

  const catalogIdToTitle = useMemo(() => buildCampaignIdToTitleMap(catalogRows), [catalogRows]);

  /** Sidebar: catalog titles only, gated by Settings → Campaigns (e.g. the 27 in “Usay Inbound”). */
  const campaigns = useMemo(
    () => visibleCampaignTitlesForStatusSidebar(catalogRows, campaignFilterVisibility),
    [catalogRows, campaignFilterVisibility]
  );

  useEffect(() => {
    if (menuType !== 'campaign' || !selectedFilter) return;
    if (!campaigns.includes(selectedFilter)) setSelectedFilter('');
  }, [menuType, campaigns, selectedFilter]);
  const teams = useMemo(() => Array.from(new Set(agentsSource.map((a) => a.team).filter(Boolean))).sort(), [agentsSource]);

  const menuItems = menuType === 'campaign' ? campaigns : teams;
  const iconMap = menuType === 'campaign' ? (statusIcons.campaigns || {}) : (statusIcons.teams || {});

  const filteredAndSorted = useMemo(() => {
    let list = agentsSource;
    if (selectedFilter) {
      if (menuType === 'campaign') {
        list = list.filter((a) =>
          agentBelongsToCampaignTitle(a.campaign, a.campaign_codes, selectedFilter, catalogIdToTitle)
        );
      } else {
        list = list.filter((a) => a.team === selectedFilter);
      }
    }
    if (loggedInOnly) {
      list = list.filter((a) => a.isLoggedIn === true);
    }
    if (statusFilter === 'available') list = list.filter((a) => isAvailableStatus(a.ipscapeStatus));
    else if (statusFilter === 'unavailable') list = list.filter((a) => !isAvailableStatus(a.ipscapeStatus));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((a) => a.name.toLowerCase().includes(q));
    }
    const sorted = [...list].sort((a, b) => {
      switch (sortBy) {
        case 'name-az':
          return a.name.localeCompare(b.name);
        case 'name-za':
          return b.name.localeCompare(a.name);
        case 'campaign-az': {
          const ac = formatCodesAsCampaignNames(a.campaign_codes ?? [], catalogIdToTitle) || a.campaign;
          const bc = formatCodesAsCampaignNames(b.campaign_codes ?? [], catalogIdToTitle) || b.campaign;
          return ac.localeCompare(bc, undefined, { sensitivity: 'base' }) || a.name.localeCompare(b.name);
        }
        case 'campaign-za': {
          const ac = formatCodesAsCampaignNames(a.campaign_codes ?? [], catalogIdToTitle) || a.campaign;
          const bc = formatCodesAsCampaignNames(b.campaign_codes ?? [], catalogIdToTitle) || b.campaign;
          return bc.localeCompare(ac, undefined, { sensitivity: 'base' }) || a.name.localeCompare(b.name);
        }
        case 'team-az':
          return a.team.localeCompare(b.team) || a.name.localeCompare(b.name);
        case 'team-za':
          return b.team.localeCompare(a.team) || a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });
    return sorted;
  }, [agentsSource, selectedFilter, menuType, statusFilter, loggedInOnly, search, sortBy, catalogIdToTitle]);

  const viewStats = useMemo(() => {
    const list = filteredAndSorted;
    const total = list.length;
    let avail = 0;
    for (const a of list) {
      if (isAvailableStatus(a.ipscapeStatus)) avail++;
    }
    return { total, avail, unavail: total - avail };
  }, [filteredAndSorted]);

  const filterContextLabel = useMemo(() => {
    if (selectedFilter) {
      return menuType === 'campaign' ? `Campaign: ${selectedFilter}` : `Team: ${selectedFilter}`;
    }
    return 'All campaigns & teams';
  }, [selectedFilter, menuType]);

  const currentUserName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');

  const toggleSidebar = () => setSidebarOpen((o) => !o);

  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        minHeight: 'calc(100vh - 52px)',
        gap: 0,
        /* Prevent aside from stretching to match the tall tile grid (which caused a huge empty scroll area). */
        alignItems: 'flex-start',
      }}
    >
      {/* Left sidebar: collapsible, flush left — height follows menu content, not main column */}
      <aside
        style={{
          width: sidebarOpen ? SIDEBAR_WIDTH_OPEN : 52,
          minWidth: sidebarOpen ? SIDEBAR_WIDTH_OPEN : 52,
          maxWidth: sidebarOpen ? SIDEBAR_WIDTH_OPEN : 52,
          flexShrink: 0,
          background: 'var(--widget-bg)',
          borderRight: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.2s ease',
          overflow: 'hidden',
          maxHeight: 'calc(100vh - 52px)',
          position: 'sticky',
          top: 0,
        }}
      >
        <div style={{ padding: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <Tooltip content={sidebarOpen ? 'Collapse menu' : 'Expand menu'}>
            <button
              type="button"
              onClick={toggleSidebar}
              style={{
                width: 36,
                height: 36,
                borderRadius: '6px',
                border: '1px solid rgba(255,255,255,0.1)',
                background: 'transparent',
                color: 'var(--text)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              aria-label={sidebarOpen ? 'Collapse menu' : 'Expand menu'}
            >
              <Menu size={20} strokeWidth={2} />
            </button>
          </Tooltip>
          {sidebarOpen && (
            <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
              {menuType === 'campaign' ? 'Campaigns' : 'Teams'}
            </span>
          )}
        </div>
        <nav
          style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '0.5rem',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {sidebarOpen ? (
            <>
              <button
                type="button"
                onClick={() => setSelectedFilter('')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  boxSizing: 'border-box',
                  padding: '0.5rem 0.75rem',
                  textAlign: 'left',
                  border: 'none',
                  borderRadius: '6px',
                  background: !selectedFilter ? 'rgba(59,130,246,0.2)' : 'transparent',
                  color: !selectedFilter ? 'var(--accent)' : 'var(--text)',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: !selectedFilter ? 600 : 400,
                  marginBottom: '0.35rem',
                }}
              >
                <LayoutList size={18} strokeWidth={2} style={{ flexShrink: 0 }} />
                All
              </button>
              <div
                style={{
                  display: 'flex',
                  gap: '0.25rem',
                  marginBottom: '0.5rem',
                  width: '100%',
                  minWidth: 0,
                  boxSizing: 'border-box',
                }}
              >
                <button
                  type="button"
                  onClick={() => { setMenuType('campaign'); setSelectedFilter(readSelectedFilter('campaign')); }}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    padding: '0.35rem 0.5rem',
                    fontSize: '0.75rem',
                    border: 'none',
                    borderRadius: '4px',
                    background: menuType === 'campaign' ? 'var(--accent)' : 'rgba(255,255,255,0.08)',
                    color: menuType === 'campaign' ? '#fff' : 'var(--text-muted)',
                    cursor: 'pointer',
                  }}
                >
                  Campaigns
                </button>
                <button
                  type="button"
                  onClick={() => { setMenuType('team'); setSelectedFilter(readSelectedFilter('team')); }}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    padding: '0.35rem 0.5rem',
                    fontSize: '0.75rem',
                    border: 'none',
                    borderRadius: '4px',
                    background: menuType === 'team' ? 'var(--accent)' : 'rgba(255,255,255,0.08)',
                    color: menuType === 'team' ? '#fff' : 'var(--text-muted)',
                    cursor: 'pointer',
                  }}
                >
                  Teams
                </button>
              </div>
              {menuItems.map((item) => {
                const IconComponent = getStatusIcon(iconMap[item]);
                return (
                  <Tooltip key={item} content={item} placement="top" fillContainer>
                    <button
                      type="button"
                      onClick={() => setSelectedFilter(item)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        boxSizing: 'border-box',
                        padding: '0.5rem 0.75rem',
                        textAlign: 'left',
                        border: 'none',
                        borderRadius: '6px',
                        background: selectedFilter === item ? 'rgba(59,130,246,0.2)' : 'transparent',
                        color: selectedFilter === item ? 'var(--accent)' : 'var(--text)',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: selectedFilter === item ? 600 : 400,
                        marginBottom: '0.25rem',
                      }}
                    >
                      <IconComponent size={18} strokeWidth={2} style={{ flexShrink: 0 }} />
                      <span
                        style={{
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                        }}
                      >
                        {item}
                      </span>
                    </button>
                  </Tooltip>
                );
              })}
            </>
          ) : (
            <>
              <Tooltip content="All" placement="right" fillContainer>
                <button
                  type="button"
                  onClick={() => setSelectedFilter('')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    maxWidth: '100%',
                    height: 36,
                    border: 'none',
                    borderRadius: '6px',
                    background: !selectedFilter ? 'rgba(59,130,246,0.2)' : 'transparent',
                    color: !selectedFilter ? 'var(--accent)' : 'var(--text-muted)',
                    cursor: 'pointer',
                    marginBottom: '0.25rem',
                    boxSizing: 'border-box',
                  }}
                >
                  <LayoutList size={20} strokeWidth={2} />
                </button>
              </Tooltip>
              <Tooltip
                content={menuType === 'campaign' ? 'Switch to teams list' : 'Switch to campaigns list'}
                placement="right"
                fillContainer
              >
                <button
                  type="button"
                  onClick={() => {
                    setMenuType((t) => {
                      const next = t === 'campaign' ? 'team' : 'campaign';
                      setSelectedFilter(readSelectedFilter(next));
                      return next;
                    });
                  }}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    height: 32,
                    border: 'none',
                    borderRadius: '4px',
                    background: 'rgba(255,255,255,0.06)',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    fontSize: '0.7rem',
                    marginBottom: '0.35rem',
                    boxSizing: 'border-box',
                  }}
                >
                  {menuType === 'campaign' ? 'C' : 'T'}
                </button>
              </Tooltip>
              {menuItems.map((item) => {
                const IconComponent = getStatusIcon(iconMap[item]);
                return (
                  <Tooltip key={item} content={item} placement="right" fillContainer>
                    <button
                      type="button"
                      onClick={() => setSelectedFilter(item)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        maxWidth: '100%',
                        height: 36,
                        border: 'none',
                        borderRadius: '6px',
                        background: selectedFilter === item ? 'rgba(59,130,246,0.2)' : 'transparent',
                        color: selectedFilter === item ? 'var(--accent)' : 'var(--text-muted)',
                        cursor: 'pointer',
                        marginBottom: '0.25rem',
                        boxSizing: 'border-box',
                      }}
                    >
                      <IconComponent size={20} strokeWidth={2} />
                    </button>
                  </Tooltip>
                );
              })}
            </>
          )}
        </nav>
      </aside>

      {/* Main content — padding so tiles don't overlap sidebar */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', padding: '1rem', overflow: 'auto' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center' }}>
          {presenceWsUrl && (
            <Tooltip
              content={
                liveBadge === 'green'
                  ? 'UC GET /agent-state is succeeding on schedule; values below reflect recent poll timings.'
                  : liveBadge === 'amber'
                    ? 'UC polling is impaired or recovering (consecutive failures or trust rebuild). Tiles may lag.'
                    : liveBadge === 'red'
                      ? 'Several UC polls failed in a row. Presence may be stale until UC recovers.'
                      : onlySocketConnected
                        ? 'WebSocket connected; UC polling may be off-hours, disabled, or not yet received.'
                        : 'Not connected to presence.'
              }
            >
              <span
                style={{
                  fontSize: '0.75rem',
                  padding: '0.25rem 0.5rem',
                  background:
                    liveBadge === 'green'
                      ? 'rgba(34,197,94,0.2)'
                      : liveBadge === 'amber'
                        ? 'rgba(234,179,8,0.15)'
                        : liveBadge === 'red'
                          ? 'rgba(239,68,68,0.18)'
                          : 'rgba(255,255,255,0.08)',
                  color:
                    liveBadge === 'green'
                      ? '#22c55e'
                      : liveBadge === 'amber'
                        ? '#eab308'
                        : liveBadge === 'red'
                          ? '#f87171'
                          : 'var(--text-muted)',
                  borderRadius: '4px',
                  fontWeight: 500,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background:
                      liveBadge === 'green'
                        ? '#22c55e'
                        : liveBadge === 'amber'
                          ? '#eab308'
                          : liveBadge === 'red'
                            ? '#ef4444'
                            : 'var(--text-muted)',
                  }}
                />
                {liveBadge === 'green'
                  ? 'Live'
                  : liveBadge === 'amber'
                    ? 'Degraded'
                    : liveBadge === 'red'
                      ? 'Stale'
                      : wallboardClosed
                        ? 'Closed'
                        : onlySocketConnected
                          ? 'UI'
                          : 'Offline'}
              </span>
            </Tooltip>
          )}
          <input
            type="search"
            placeholder="Search by name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid var(--widget-bg)', minWidth: '180px' }}
          />
          {/* Status filter: All / Available / Unavailable (ipSCAPE) */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0,
              padding: '2px',
              borderRadius: '8px',
              border: '1px solid var(--widget-bg)',
              background: 'var(--widget-bg)',
            }}
          >
            {(
              [
                { value: 'all' as const, label: 'All', Icon: Grid3X3 },
                { value: 'available' as const, label: 'Available', Icon: CheckCircle },
                { value: 'unavailable' as const, label: 'Unavailable', Icon: XCircle },
              ] as const
            ).map(({ value, label, Icon }) => {
              const selected = statusFilter === value;
              return (
                <Tooltip key={value} content={label} placement="bottom">
                  <button
                    type="button"
                    onClick={() => setStatusFilter(value)}
                    aria-label={label}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 36,
                      height: 36,
                      border: 'none',
                      borderRadius: '6px',
                      background: selected ? 'var(--bg)' : 'transparent',
                      color: selected ? 'var(--accent)' : 'var(--text-muted)',
                      cursor: 'pointer',
                      boxShadow: selected ? '0 1px 2px rgba(0,0,0,0.2)' : undefined,
                    }}
                  >
                    <Icon size={20} strokeWidth={2} />
                  </button>
                </Tooltip>
              );
            })}
          </div>
          <Tooltip
            content={
              loggedInOnly
                ? 'Showing agents likely logged into ipSCAPE (available, paused, on campaign, etc.). Turn off to include idle Unavailable stations.'
                : 'Showing all agents from the last UC roster. Turn on to hide likely logged-out Unavailable stations.'
            }
          >
            <button
              type="button"
              onClick={() => setLoggedInOnly((v) => !v)}
              aria-label={loggedInOnly ? 'Logged in only' : 'All agents'}
              aria-pressed={loggedInOnly}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                border: '1px solid var(--widget-bg)',
                borderRadius: '6px',
                background: loggedInOnly ? 'rgba(59,130,246,0.2)' : 'var(--widget-bg)',
                color: loggedInOnly ? 'var(--accent)' : 'var(--text-muted)',
                cursor: 'pointer',
              }}
            >
              <UserCheck size={20} strokeWidth={2} />
            </button>
          </Tooltip>
          <Tooltip content="Sort by">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              style={{ padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid var(--widget-bg)', background: 'var(--bg)', color: 'var(--text)' }}
            >
            <option value="name-az">Name A–Z</option>
            <option value="name-za">Name Z–A</option>
            <option value="campaign-az">Campaign A–Z</option>
            <option value="campaign-za">Campaign Z–A</option>
            <option value="team-az">Team A–Z</option>
            <option value="team-za">Team Z–A</option>
            </select>
          </Tooltip>
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.75rem 1.25rem',
            marginBottom: '0.75rem',
            padding: '0.5rem 0.75rem',
            borderRadius: '8px',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.06)',
            fontSize: '0.8125rem',
            color: 'var(--text-muted)',
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem 1.25rem' }}>
            <span>
              <strong style={{ color: 'var(--text)' }}>{filterContextLabel}</strong>
            </span>
            <span aria-hidden style={{ opacity: 0.35 }}>
              ·
            </span>
            <span>
              In view: <strong style={{ color: 'var(--text)' }}>{viewStats.total}</strong>
            </span>
            <span>
              Available: <strong style={{ color: '#22c55e' }}>{viewStats.avail}</strong>
            </span>
            <span>
              Unavailable / other: <strong style={{ color: '#eab308' }}>{viewStats.unavail}</strong>
            </span>
          </div>
          {(lastUcPollUi != null ||
            wsFeedMeta?.ucPollAvgDurationMs != null ||
            wsFeedMeta?.ucPollDurationMs != null) && (
            <Tooltip
              content={
                'Clock is when the Presence worker last completed a poll tick (every interval from Settings). ' +
                'Agent tiles refresh only on a successful UC fetch; skipped/failed ticks still update this clock. ' +
                (wsFeedMeta?.ucPollSkippedReason
                  ? ` Last tick: no UC fetch (${wsFeedMeta.ucPollSkippedReason}) — enable polling in Settings → Operating hours.`
                  : '')
              }
            >
              <span
                style={{
                  fontSize: '0.72rem',
                  opacity: 0.85,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: '0.35rem 0.65rem',
                  textAlign: 'right',
                }}
              >
                {lastUcPollUi != null && (
                  <span style={{ whiteSpace: 'nowrap' }}>
                    Last update{' '}
                    <strong style={{ color: 'var(--text)' }}>{lastUcPollUi.clock}</strong>
                    <span style={{ opacity: 0.9 }}> ({lastUcPollUi.ago})</span>
                  </span>
                )}
                {(wsFeedMeta?.ucPollAvgDurationMs != null || wsFeedMeta?.ucPollDurationMs != null) && (
                  <span style={{ whiteSpace: 'nowrap' }}>
                    {lastUcPollUi != null ? '· ' : ''}UC{' '}
                    {wsFeedMeta.ucPollAvgDurationMs != null && (
                      <>
                        ~<strong style={{ color: 'var(--text)' }}>{wsFeedMeta.ucPollAvgDurationMs}ms</strong> avg
                      </>
                    )}
                    {wsFeedMeta.ucPollAvgDurationMs != null && wsFeedMeta.ucPollDurationMs != null && ' · '}
                    {wsFeedMeta.ucPollDurationMs != null && (
                      <>
                        last <strong style={{ color: 'var(--text)' }}>{wsFeedMeta.ucPollDurationMs}ms</strong>
                      </>
                    )}
                  </span>
                )}
              </span>
            </Tooltip>
          )}
        </div>

        {snapshotMode && (
          <div
            role="status"
            style={{
              marginBottom: '0.75rem',
              padding: '0.65rem 0.85rem',
              borderRadius: '8px',
              background: 'rgba(100, 116, 139, 0.15)',
              border: '1px solid rgba(148, 163, 184, 0.35)',
              fontSize: '0.8125rem',
              color: 'var(--text-muted)',
              lineHeight: 1.45,
            }}
          >
            <strong style={{ color: 'var(--text)' }}>
              {wallboardClosed ? 'Call centre closed' : 'Live feed paused'}
            </strong>
            {' — '}
            {closedReasonLabel}. Tiles show a snapshot
            {lastUcPollUi ? (
              <>
                {' '}
                from <strong style={{ color: 'var(--text)' }}>{lastUcPollUi.clock}</strong> ({lastUcPollUi.ago}).
              </>
            ) : (
              '.'
            )}{' '}
            Status times are not updating until polling resumes.
            {wsFeedMeta?.platformUsersSyncedAt ? (
              <>
                {' '}
                Login flags last synced{' '}
                <strong style={{ color: 'var(--text)' }}>{wsFeedMeta.platformUsersSyncedAt}</strong>.
              </>
            ) : null}
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '1rem',
            alignItems: 'start',
          }}
        >
          {filteredAndSorted.map((agent) => (
            <AgentTile
              key={agent.id}
              agent={agent}
              snapshotMode={snapshotMode}
              avatarUrl={agent.name === currentUserName ? (user?.imageUrl ?? undefined) : undefined}
            />
          ))}
        </div>

        {agentsSource.length === 0 && (
          <Tooltip content="Use Settings → Agents → Refresh list to cache agents from UC when polling is off.">
            <p style={{ color: 'var(--text-muted)', marginTop: '1rem', cursor: 'default', display: 'inline-block' }}>No agents.</p>
          </Tooltip>
        )}
        {agentsSource.length > 0 && filteredAndSorted.length === 0 && (
          <p style={{ color: 'var(--text-muted)', marginTop: '1rem' }}>No agents match the current filters.</p>
        )}
      </div>
    </div>
  );
}
